import { FieldDefinition, Rule } from '../../models/endpoints';
import { renderHtml } from '../../utils/renderHtml';
import { type SchemaObjectWithName } from './types';
import { default as Collapse } from 'antd/es/collapse';

function Label({
  name,
  type,
  format,
  required
}: {
  name: string;
  type?: string;
  format?: string;
  required: boolean;
}) {
  return (
    <div className="flex flex-row items-center">
      <div className="flex items-center gap-2 text-nowrap">
        <code className="font-bold">{name}</code>
        <code className="text-ellipsis text-inpay-black-primary-600">
          {type}
        </code>
        {format && <code className="font-bold">{format}</code>}
        {required ? <span className="required">required</span> : null}
      </div>
    </div>
  );
}

function Collapsible({
  name,
  type,
  required,
  children
}: {
  name: string;
  type?: string;
  required: boolean;
  children: JSX.Element;
}) {
  return (
    <Collapse
      className="min-w-fit"
      data-testid={`collapsible-${name}`}
      items={[
        {
          key: name,
          label: <Label name={name} type={type} required={required} />,
          children: children ? children : null
        }
      ]}
    />
  );
}

export function OpenAPISchema({ field }: { field: SchemaObjectWithName }) {
  /**
   * Handle array objects
   *
   */

  // handle special case where the array is a primitive type
  if (field.type === 'array' && !field.items?.properties) {
    return (
      <Collapsible
        name={field.name}
        type={field.type ?? ''}
        required={field?.customRequired ?? false}
      >
        <code className="text-ellipsis text-inpay-black-primary-600">
          {field.items?.type}
        </code>
      </Collapsible>
    );
  }

  if (field.type === 'array' && field.items?.properties) {
    const children = Object.entries(field.items?.properties).map(
      ([key, value]) => (
        <div key={key} className="my-4">
          <OpenAPISchema
            field={{ name: key, customRequired: false, ...(value as object) }}
          />
        </div>
      )
    );

    return (
      <Collapsible
        name={field.name}
        type={field.type}
        required={field?.customRequired ?? false}
      >
        <>{children}</>
      </Collapsible>
    );
  }

  /**
   * Handle object objects
   *
   */
  if (field.type === 'object' && field.properties) {
    const children = Object.entries(field.properties).map(([key, value]) => {
      return (
        <div key={key} className="my-4">
          <OpenAPISchema
            field={
              {
                name: key,
                customRequired: field?.required?.includes(key) ?? false,
                ...value
              } as SchemaObjectWithName
            }
          />
        </div>
      );
    });

    return (
      <Collapsible
        name={field.name}
        type={field.type}
        required={field?.customRequired ?? false}
      >
        <>{children}</>
      </Collapsible>
    );
  }

  /**
   * special handler for create payout
   */
  if (field.type === 'Object' && field.fields) {
    const children = field.fields.map((field: FieldDefinition) => (
      <div key={field.field} className="my-4">
        <OpenAPISchema
          field={
            {
              name: field.field,
              customRequired: false,
              ...field
            } as SchemaObjectWithName
          }
        />
      </div>
    ));

    return (
      <Collapsible name={field.name} type={field.type} required={true}>
        <>{children}</>
      </Collapsible>
    );
  }

  /**
   * These rules are Create Payout specific
   */
  const isRequired =
    field?.rules?.some((rule: Rule) => rule.description === 'Required') ||
    field?.customRequired ||
    field.required;

  const nonRequiredRules =
    field?.rules?.filter((rule: Rule) => rule.description !== 'Required') || [];

  /**
   * Handle everything else (number, string)
   *
   */
  return (
    <div className="my-2 flex max-w-md flex-col">
      <div
        className="flex flex-col gap-4"
        data-testid={`field-${field.name}-${field.type}`}
      >
        <Label
          name={field.name}
          type={field.type}
          required={isRequired}
          format={field.format}
        />
        {field.description ? (
          <div className="flex flex-row">{renderHtml(field.description)}</div>
        ) : null}
        {nonRequiredRules.length > 0 && (
          <>
            <p className="text-inpay-black-primary-800 underline">Rules:</p>
            {nonRequiredRules.map((rule: Rule, index: number) => (
              <div key={`rule_${index}`}>
                {renderHtml(rule.description, 'text-inpay-black-primary-800')}
                {rule.hint &&
                  renderHtml(rule.hint, 'text-inpay-black-primary-800')}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
